import type { FC, MouseEventHandler } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { useHistory } from "react-router";

import { toastError } from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { patchDetails } from "~/v2/api/rest/account";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { usePageTimer } from "../../../entityOnboarding/usePageTimer";
import { Layout } from "../../layout";
import { ProgressionButton } from "../../ProgressionButton";
import { RadiosInput } from "../../RadiosInput";
import { InvestmentExperienceStep } from "../../steps";
import { logAmplitudeEvent } from "../../util";
import { type SpreadForm, zSpreadForm } from "./schema";

const LABEL = "Do you understand options spreads and their uses?";
const OPTIONS = [
  {
    value: "yes",
    label: "Yes, I've entered spreads before, or know how they work",
  },
  { value: "no", label: "No, I'm not sure how spreads work" },
];

export const SpreadsPage: FC = () => {
  const history = useHistory();

  const { currentAccount } = useAccountContext({ liveOnly: true });

  const pageTimer = usePageTimer("spreads_page");

  const form = useForm<SpreadForm>({
    mode: "onChange",
    resolver: zodResolver(zSpreadForm),
  });

  const mutation = useMutationRequest(
    (payload: SpreadForm) => patchDetails(currentAccount?.id ?? "", payload),
    {
      onSuccess: () => {
        const duration = pageTimer.stop();
        logAmplitudeEvent.pageCompleted(duration);

        history.push(Path.ROUTE_OPTIONS_OPT_IN_AGREEMENT);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Error: ${errorMessage}`);
      },
    },
  );

  const onBackClicked: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    pageTimer.stop();
    logAmplitudeEvent.backClicked();
    history.push(Path.ROUTE_OPTIONS_OPT_IN_INVESTMENT_EXPERIENCE);
  };

  const onSubmitInvalid: SubmitErrorHandler<SpreadForm> = (_errors) => {
    toastError("Error: Invalid Form. Please fill out all required fields");
  };

  const onSubmit: SubmitHandler<SpreadForm> = (values) => {
    logAmplitudeEvent.nextClicked();
    mutation.mutate(values);
  };

  const onSelect = (value: string) =>
    form.setValue("has_spreads_knowledge", value === "yes");

  const hasSpreadKnowledge = form.watch("has_spreads_knowledge");

  return (
    <Layout currentStep={InvestmentExperienceStep}>
      <form onSubmit={form.handleSubmit(onSubmit, onSubmitInvalid)}>
        <RadiosInput
          id="has_spreads_knowledge"
          label={LABEL}
          value={
            typeof hasSpreadKnowledge !== "boolean"
              ? ""
              : hasSpreadKnowledge
                ? "yes"
                : "no"
          }
          options={OPTIONS}
          onSelect={onSelect}
        />

        <div className="flex max-w-lg items-center justify-between pt-4">
          <ProgressionButton
            variant="secondary"
            onClick={onBackClicked}
            type="button"
          >
            Back
          </ProgressionButton>
          <ProgressionButton
            type="submit"
            disabled={typeof hasSpreadKnowledge !== "boolean"}
          >
            Next
          </ProgressionButton>
        </div>
      </form>
    </Layout>
  );
};

export default SpreadsPage;
